import React, {
  Dispatch,
  PropsWithChildren,
  useContext,
  useReducer,
} from "react";
// import { entitiesTree } from "../mock/entities-tree";
import { IEntitiesTreeItem } from "../models/entity.interface";
import organizationService from "../services/organization.service";

interface IManageEntitiesState {
  loading: boolean;
  entities: IEntitiesTreeItem[];
  totalEntities: number;
  totalUsers: number;
  totalActiveUsers: number;
  selectedEntity: IEntitiesTreeItem | null;
  entityModal: {
    show: boolean;
    mode?: "add" | "edit";
  };
  showDeleteEntityModal: boolean;
}

export enum ManageEntitiesActionType {
  SET_LOADING = "SET_LOADING",
  SET_ENTITIES = "SET_ENTITIES",
  SET_SELECTED_ENTITY = "SET_SELECTED_ENTITY",
  SET_ENTITY_MODAL = "SET_ENTITY_MODAL",
  SET_DELETE_ENTITY_MODAL = "SET_DELETE_ENTITY_MODAL",
}

type ManageEntitiesReducerAction =
  | {
      type: ManageEntitiesActionType.SET_LOADING;
      payload: boolean;
    }
  | {
      type: ManageEntitiesActionType.SET_ENTITIES;
      payload: {
        entityTree: IEntitiesTreeItem[];
        totalEntities: number;
        totalUsers: number;
        totalActiveUsers: number;
      };
    }
  | {
      type: ManageEntitiesActionType.SET_SELECTED_ENTITY;
      payload: IEntitiesTreeItem | null;
    }
  | {
      type: ManageEntitiesActionType.SET_ENTITY_MODAL;
      payload: {
        show: boolean;
        mode?: "add" | "edit";
      };
    }
  | {
      type: ManageEntitiesActionType.SET_DELETE_ENTITY_MODAL;
      payload: boolean;
    };

interface IManageEntitiesContext {
  state: IManageEntitiesState;
  dispatch: Dispatch<ManageEntitiesReducerAction>;
  loadEntities: () => Promise<void>;
}

const initState: IManageEntitiesState = {
  loading: false,
  entities: [],
  selectedEntity: null,
  totalEntities: 0,
  entityModal: {
    show: false,
  },
  totalUsers: 0,
  totalActiveUsers: 0,
  showDeleteEntityModal: false,
};

const ManageEntitiesContext = React.createContext<IManageEntitiesContext>({
  state: initState,
  dispatch: () => {},
  loadEntities: () => new Promise((resolve) => resolve()),
});

const manageEntitiesReducer = (
  state: IManageEntitiesState,
  action: ManageEntitiesReducerAction
): IManageEntitiesState => {
  switch (action.type) {
    case ManageEntitiesActionType.SET_LOADING:
      return { ...state, loading: action.payload };
    case ManageEntitiesActionType.SET_ENTITIES:
      return {
        ...state,
        entities: action.payload.entityTree,
        totalEntities: action.payload.totalEntities,
        totalUsers: action.payload.totalUsers,
        totalActiveUsers: action.payload.totalActiveUsers,
      };
    case ManageEntitiesActionType.SET_SELECTED_ENTITY:
      return { ...state, selectedEntity: action.payload };
    case ManageEntitiesActionType.SET_ENTITY_MODAL:
      return { ...state, entityModal: action.payload };
    case ManageEntitiesActionType.SET_DELETE_ENTITY_MODAL:
      return { ...state, showDeleteEntityModal: action.payload };
    default:
      return state;
  }
};

type ManageEntitiesContextProviderProps = PropsWithChildren<{ orgId: string }>;

export const ManageEntitiesContextProvider = ({
  orgId,
  children,
}: ManageEntitiesContextProviderProps) => {
  const [state, dispatch] = useReducer(manageEntitiesReducer, initState);

  const loadEntities = async () => {
    try {
      dispatch({
        type: ManageEntitiesActionType.SET_LOADING,
        payload: true,
      });

      const data = await organizationService.getOrgEntitiesTree(orgId);
      if (!data) {
        return;
      }
      const { tree, totalEntities, totalUsers, totalActiveUsers } = data;

      dispatch({
        type: ManageEntitiesActionType.SET_ENTITIES,
        payload: {
          entityTree: tree,
          totalEntities,
          totalUsers,
          totalActiveUsers,
        },
      });
    } finally {
      dispatch({
        type: ManageEntitiesActionType.SET_LOADING,
        payload: false,
      });
    }
  };
  return (
    <ManageEntitiesContext.Provider value={{ state, dispatch, loadEntities }}>
      {children}
    </ManageEntitiesContext.Provider>
  );
};

export const useManageEntitiesContext = () => useContext(ManageEntitiesContext);
